'use client';

import { rubik } from '@/app/utils/fonts';

import { Milestone } from '@/app/api';
import { PropsWithClassname } from '@/app/types';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface MilestoneSelectorProps extends PropsWithClassname {
  milestones: Milestone[];
  selectedMilestone: Milestone;
  onSelectMilestone: (nextMilestone: Milestone) => void;
}

const MilestoneSelector = ({ selectedMilestone, onSelectMilestone, milestones, className }: MilestoneSelectorProps) => {
  const { t } = useTranslation();

  const isFirstMilestoneSelected = useMemo(() => {
    if (!milestones || !selectedMilestone) {
      return false;
    }

    const currentMilestoneIndex = milestones.findIndex(
      (milestone) => milestone.amountInCents === selectedMilestone.amountInCents,
    );

    return currentMilestoneIndex === 0;
  }, [milestones, selectedMilestone]);

  const isLastMilestoneSelected = useMemo(() => {
    if (!milestones || !selectedMilestone) {
      return false;
    }

    const currentMilestoneIndex = milestones.findIndex(
      (milestone) => milestone.amountInCents === selectedMilestone.amountInCents,
    );

    return currentMilestoneIndex + 1 === milestones.length;
  }, [milestones, selectedMilestone]);

  const handleSelectPreviousMilestone = useCallback(() => {
    if (!milestones || !selectedMilestone) {
      return;
    }

    const currentMilestoneIndex = milestones.findIndex(
      (milestone) => milestone.amountInCents === selectedMilestone.amountInCents,
    );

    if (isFirstMilestoneSelected) {
      return;
    }

    onSelectMilestone(milestones[currentMilestoneIndex - 1]);
  }, [milestones, selectedMilestone, onSelectMilestone, isFirstMilestoneSelected]);

  const handleSelectNextMilestone = useCallback(() => {
    if (!milestones || !selectedMilestone) {
      return;
    }

    const currentMilestoneIndex = milestones.findIndex(
      (milestone) => milestone.amountInCents === selectedMilestone.amountInCents,
    );

    if (isLastMilestoneSelected) {
      return;
    }

    onSelectMilestone(milestones[currentMilestoneIndex + 1]);
  }, [milestones, selectedMilestone, onSelectMilestone, isLastMilestoneSelected]);

  return (
    <div className={className}>
      <div className="flex justify-between items-center mb-[16px]">
        <h2
          className={classNames('font-medium text-[12px] tracking-[4.5px] leading-[24px] uppercase', rubik.className)}
        >
          {t('components.MilestoneSelector.milestone', {
            current:
              milestones.findIndex((milestone) => milestone.amountInCents === selectedMilestone?.amountInCents) + 1,
            goal: milestones.length,
          })}
        </h2>
        {milestones.length > 1 && (
          <div className="flex items-stretch">
            <button
              className={classNames('p-[16px] group', {
                'cursor-pointer': !isFirstMilestoneSelected,
                'cursor-default': isFirstMilestoneSelected,
              })}
              onClick={handleSelectPreviousMilestone}
            >
              <span
                className={classNames(
                  'block w-0 h-0 border border-t-[5px] border-r-[4px] border-b-[5px] border-l-0 border-l-[transparent] border-t-[transparent] border-b-[transparent] transition-all',
                  {
                    'border-r-neutral-dark group-hover:border-r-primary-mid': !isFirstMilestoneSelected,
                    'border-r-neutral-semiTransparent': isFirstMilestoneSelected,
                  },
                )}
              />
            </button>
            <button
              className={classNames('p-[16px] group', {
                'cursor-pointer': !isLastMilestoneSelected,
                'cursor-default': isLastMilestoneSelected,
              })}
              onClick={handleSelectNextMilestone}
            >
              <span
                className={classNames(
                  'block w-0 h-0 border border-t-[5px] border-r-0 border-b-[5px] border-l-[4px] border-r-[transparent] border-t-[transparent] border-b-[transparent] transition-all',
                  {
                    'border-l-neutral-dark group-hover:border-l-primary-mid': !isLastMilestoneSelected,
                    'border-l-neutral-semiTransparent': isLastMilestoneSelected,
                  },
                )}
              />
            </button>
          </div>
        )}
      </div>
      <p className="mb-[16px] line-clamp-3 ellipsis">{selectedMilestone.text}</p>
    </div>
  );
};

export default MilestoneSelector;
